import React, { useEffect, useState } from 'react';
import ArticlesService, { Article } from '../../services/articlesService';
import { formatPrixByLot } from '../../utils/priceUtils';
import Button from '../Button/Button';
// PIXEL-PARFAIT : on reutilise le style exact de la modale (memes classes .article-row / .article-code
// / .article-nom / .quantity-* ...). Aucun style maison pour les lignes.
import '../ModalArticles/ModalArticles.css';
import './CataloguePreview.css';

interface CataloguePreviewProps {
  // Lot technique + departement (code) de l'operation : resolvent le titulaire rang 1 et son catalogue.
  // Utilises cote ACHETEUR (qui compose avant transfert, sans contrat propre).
  idLotTechnique?: number;
  idDepartement?: number;
  // Optionnel : commande reelle (porte le contrat). Fournie cote PRESTATAIRE -> le catalogue est
  // resolu PAR CONTRAT, donc au RANG CONSULTE (exactement comme la modale), et jamais les prix des
  // rangs concurrents. Prioritaire sur (idLotTechnique, idDepartement) qui, eux, resolvent le rang 1.
  commande?: any;
  // Ouverture de la modale complete (ModalArticles) : un clic n'importe ou sur la preview la declenche.
  onOpen: () => void;
  // Nom du lot, affiche dans le titre comme la modale ("Catalogue - {lotNom}").
  lotNom?: string;
  // Nombre d'articles montres en apercu (defaut 5).
  max?: number;
}

// Apercu du catalogue d'un lot rendu DANS LA PAGE, au pixel pres comme la modale ModalArticles :
// memes lignes d'articles (code / nom / prix de base / controles de quantite), limitees a `max`.
// Les controles sont neutralises (pointer-events cotes CSS) ; un clic n'importe ou ouvre la vraie modale.
const CataloguePreview: React.FC<CataloguePreviewProps> = ({ idLotTechnique, idDepartement, commande, onOpen, lotNom, max = 5 }) => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  // Source du catalogue : la commande (contrat -> rang consulte) prime ; sinon (lot, dept) -> rang 1.
  const parContrat = !!commande;
  const pretARequeter = parContrat || (!!idLotTechnique && !!idDepartement);

  useEffect(() => {
    if (!pretARequeter) {
      setArticles([]);
      setTotal(0);
      return undefined;
    }
    let annule = false;
    setLoading(true);
    const commandes = parContrat
      ? [commande]
      : [{ _catalogueLotQuery: { id_lot_technique: idLotTechnique, id_departement: idDepartement } }];
    ArticlesService.getArticlesByCommandes(commandes)
      .then((list) => {
        if (annule) return;
        setTotal(list.length);
        setArticles(list.slice(0, max));
      })
      .catch(() => {
        if (!annule) {
          setArticles([]);
          setTotal(0);
        }
      })
      .finally(() => {
        if (!annule) setLoading(false);
      });
    return () => {
      annule = true;
    };
  }, [pretARequeter, parContrat, commande, idLotTechnique, idDepartement, max]);

  if (!pretARequeter) return null;

  return (
    <div
      className="catalogue-preview"
      role="button"
      tabIndex={0}
      title="Ouvrir le catalogue"
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="catalogue-preview__header">
        <span className="catalogue-preview__titre">{lotNom ? `Catalogue - ${lotNom}` : 'Catalogue'}</span>
      </div>

      {loading ? (
        <div className="catalogue-preview__loading">Chargement du catalogue…</div>
      ) : articles.length === 0 ? (
        <div className="catalogue-preview__vide">Aucun article disponible pour ce lot.</div>
      ) : (
        <>
          {/* Lignes IDENTIQUES a la modale (.article-row). Neutralisees via .catalogue-preview (CSS). */}
          <div className="articles-list">
            {articles.map((article) => (
              <div key={article.id} className="article-container">
                <div className="article-row">
                  <div className="article-code">{article.code}</div>
                  <div className="article-info">
                    <h4 className="article-nom">{article.nom}</h4>
                    <p className="article-prix">
                      <span className="prix-value">
                        {formatPrixByLot(ArticlesService.getPrixByQuantity(article, 1), article.unite, article.id_lot_technique, false)} €
                      </span>{' '}
                      / {article.unite}
                    </p>
                  </div>
                  <div className="quantity-controls">
                    <button className="quantity-btn decrease" type="button" tabIndex={-1}>−</button>
                    <input type="text" className="quantity-input" value="0" readOnly tabIndex={-1} />
                    <button className="quantity-btn increase" type="button" tabIndex={-1}>+</button>
                  </div>
                  <div className="article-total">
                    <div className="total-value" />
                  </div>
                  <div>
                    <Button value="Détails" variant="secondary" size="small" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {total > articles.length && (
            <div className="catalogue-preview__more">
              + {total - articles.length} autres articles - cliquez pour tout voir
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CataloguePreview;
